import { Color, Spacing } from '@persona-id/design-tokens';
import { Dictionary } from 'lodash';
import { Dispatch, SetStateAction, useCallback } from 'react';
import styled from 'styled-components';

import DiffIcon from 'dashboard/apps/InquiryTemplateEditor/DiffIcon';
import DiffStatusSticker from 'dashboard/apps/InquiryTemplateEditor/DiffStatusSticker';
import { SelectedComponentAndStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { ScreenItemConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/types';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import {
  StepDiff,
  VersionDiffChangeType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { uiStepAllComponentConfigs } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import { ValueDiffLabel } from 'dashboard/apps/InquiryTemplateEditor/ValueDiffLabel';
import { PropertyRow } from 'dashboard/apps/InquiryTemplateEditor/VersionDiffTable';
import Accordion from 'dashboard/components/Accordion';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
} from 'dashboard/models/inquiry-template-version';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';

const StepPanelCardContainer = styled.div`
  padding: ${Spacing[2]} ${Spacing[3]};
  padding-bottom: 0px;
`;

const AccordionTitle = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StepPropertyContainer = styled.div`
  padding: 0px ${Spacing[3]};
`;

const StepComponent = styled.div`
  cursor: pointer;
  margin: ${Spacing[2]};
  height: 20px;
  display: flex;
  align-items: center;
  gap: ${Spacing[1]};
  text-decoration: underline;
`;

const StyledParagraph = styled(Paragraph)<{
  changeType: VersionDiffChangeType | undefined;
  selected: boolean;
}>`
  :hover {
    color: ${Color.CorePurple800};
  }

  ${({ selected }) => {
    if (selected) return `color: ${Color.CorePurple800};`;
  }}
  ${({ changeType }) => {
    if (!changeType) return `padding-left: 20px;`;
  }}
`;

interface StepDiffPropertyCardProps {
  stepDiff: StepDiff | undefined;
  targetStep: InquiryTemplateVersionStepUi | undefined;
  baseStep: InquiryTemplateVersionStepUi | undefined;
  targetStepsMap: Dictionary<InquiryTemplateVersionStep>;
  baseStepsMap: Dictionary<InquiryTemplateVersionStep>;
  setSelectedStep: (navConfig: ScreenItemConfig) => void;
  setSelectedComponentAndStep: Dispatch<SetStateAction<SelectedComponentAndStep>>;
  screenItemConfig: ScreenItemConfig | undefined;
  showUnchanged?: boolean;
  isSelectedStep: boolean;
  selectedComponentName: string | undefined;
}

const StepDiffPropertyCard = ({
  stepDiff,
  targetStep,
  baseStep,
  targetStepsMap,
  baseStepsMap,
  setSelectedStep,
  setSelectedComponentAndStep,
  screenItemConfig,
  showUnchanged = false,
  isSelectedStep,
  selectedComponentName,
}: StepDiffPropertyCardProps) => {
  const setComponent = useCallback(
    (componentName: string) => {
      const targetComponents = targetStep
        ? uiStepAllComponentConfigs(targetStep as InquiryTemplateVersionStepUi)
        : [];
      const baseComponents = baseStep
        ? uiStepAllComponentConfigs(baseStep as InquiryTemplateVersionStepUi)
        : [];
      const component = [...targetComponents, ...baseComponents].find(
        // Component names have various name formats, inflect to kebab for consistency
        (component) => component.name.replace(/_/g, '-') === componentName.replace(/_/g, '-'),
      );

      if (component && screenItemConfig) {
        setSelectedComponentAndStep({
          component,
          step: (targetStep || baseStep) as InquiryTemplateVersionStepUi,
          error: false,
        });
      }
    },
    [setSelectedComponentAndStep, targetStep, baseStep, screenItemConfig],
  );

  const StepDiffComponent = ({
    componentName,
    changeType,
  }: {
    componentName: string;
    changeType: VersionDiffChangeType | undefined;
  }) => {
    return (
      <StepComponent
        onClick={() => setComponent(componentName)}
        id={`version_diff__step_component_${componentName}`}
      >
        {changeType && <DiffIcon changeType={changeType} />}
        <StyledParagraph
          weight='bold'
          color='primary'
          size='lg'
          changeType={changeType}
          selected={selectedComponentName?.replace(/-/g, '_') === componentName}
        >
          {componentName}
        </StyledParagraph>
      </StepComponent>
    );
  };

  const StepDiffUnmodifiedPropertyContent = ({
    step,
    stepMap,
    changeType,
  }: {
    step: InquiryTemplateVersionStepUi;
    stepMap: Dictionary<InquiryTemplateVersionStep>;
    changeType: VersionDiffChangeType | undefined;
  }) => (
    <>
      <Spacer height={1} />
      <Label weight='bold'>Back Step Configuration</Label>
      <PropertyRow
        textKey='Hide Cancel Button'
        value={step.config['hide-cancel-button']?.toString() || 'None'}
      />
      <PropertyRow
        textKey='Back Step'
        value={
          step?.config['back-step']
            ? getDisplayNameForStep(stepMap[step.config['back-step']])
            : 'None'
        }
      />
      <Label weight='bold'>Components</Label>
      {step.config.components.map((component) => (
        <StepDiffComponent
          key={component.name}
          componentName={component.name}
          changeType={changeType}
        />
      ))}
    </>
  );

  let panelContents = null;
  switch (stepDiff?.['change-type']) {
    case VersionDiffChangeType.MODIFIED:
      panelContents = (
        <>
          <Spacer height={1} />
          <Label weight='bold'>Back Step Configuration</Label>
          <PropertyRow
            textKey='Hide Cancel Button'
            value={
              <ValueDiffLabel
                valueDiff={stepDiff.changes['hide-cancel-button']}
                fallbackValue={targetStep?.config['hide-cancel-button']?.toString() || 'None'}
              />
            }
          />
          <PropertyRow
            textKey='Back Step'
            value={
              <ValueDiffLabel
                valueDiff={{
                  added: stepDiff.changes['back-step'].added
                    ? getDisplayNameForStep(targetStepsMap[stepDiff.changes['back-step'].added])
                    : null,
                  removed: stepDiff.changes['back-step'].removed
                    ? getDisplayNameForStep(baseStepsMap[stepDiff.changes['back-step'].removed])
                    : null,
                }}
                fallbackValue={
                  targetStep?.config['back-step']
                    ? getDisplayNameForStep(targetStepsMap[targetStep.config['back-step']])
                    : 'None'
                }
                vertical
              />
            }
          />
          <Label weight='bold'>Components</Label>
          {stepDiff.changes.components &&
            Object.entries(stepDiff.changes.components).map(([componentName, componentDiff]) => (
              <StepDiffComponent
                key={componentName}
                componentName={componentName}
                changeType={componentDiff['change-type']}
              />
            ))}
          {showUnchanged &&
            targetStep?.config.components
              .filter((component) => !(component.name in stepDiff.changes.components))
              .map((component) => {
                return (
                  <StepDiffComponent
                    key={component.name}
                    componentName={component.name}
                    changeType={undefined}
                  />
                );
              })}
        </>
      );
      break;
    case VersionDiffChangeType.ADDED:
      panelContents = targetStep && (
        <StepDiffUnmodifiedPropertyContent
          step={targetStep}
          changeType={VersionDiffChangeType.ADDED}
          stepMap={targetStepsMap}
        />
      );
      break;
    case VersionDiffChangeType.REMOVED:
      panelContents = baseStep && (
        <StepDiffUnmodifiedPropertyContent
          step={baseStep}
          stepMap={baseStepsMap}
          changeType={VersionDiffChangeType.ADDED}
        />
      );
      break;
    default:
      panelContents = targetStep && (
        <StepDiffUnmodifiedPropertyContent
          step={targetStep}
          changeType={undefined}
          stepMap={targetStepsMap}
        />
      );
      break;
  }

  return (
    <StepPanelCardContainer>
      <Accordion
        defaultExpanded={false}
        onTitleClick={() => {
          if (screenItemConfig) {
            setSelectedStep(screenItemConfig);
          }
        }}
        caretSize='md'
        overrideExpanded={isSelectedStep}
        title={
          <AccordionTitle>
            <Paragraph weight='bold' color='primary' size='lg'>
              {targetStep?.['name-display'] || baseStep?.['name-display']}
            </Paragraph>
            {stepDiff?.['change-type'] && (
              <DiffStatusSticker changeType={stepDiff['change-type']} />
            )}
          </AccordionTitle>
        }
        titleGapSize={Spacing[0]}
      >
        <StepPropertyContainer>{panelContents}</StepPropertyContainer>
      </Accordion>
    </StepPanelCardContainer>
  );
};

export default StepDiffPropertyCard;
